/**
 * Global keyboard shortcuts for power users.
 *
 * / — focus command bar
 * g then h — go home (command centre)
 * g then c — go to cloud
 * g then y — go to cyber
 * g then w — go to world
 * g then t — go to trends
 * g then n — go to news
 * g then b — go to briefing
 * g then s — go to signals
 * g then i — go to AI insights
 * ? — toggle help panel
 */
import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'

const GO_SHORTCUTS: Record<string, string> = {
  h: '/app',
  c: '/app/cloud',
  y: '/app/cyber',
  w: '/app/world',
  t: '/app/trends',
  n: '/app/tech-news',
  b: '/app/briefing',
  s: '/app/signals',
  i: '/app/ai-insights',
  r: '/app/tech-radar',
  f: '/app/for-you',
  k: '/app/workspace',
  m: '/app/terminal',
  p: '/app/pricing-trends',
  a: '/app/admin',
  d: '/app/what-changed',
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate()
  const waitingForG = useRef(false)
  const gTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement
      // Don't capture when typing in inputs
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable) return

      // g + key navigation
      if (waitingForG.current) {
        waitingForG.current = false
        if (gTimer.current) clearTimeout(gTimer.current)
        const dest = GO_SHORTCUTS[e.key]
        if (dest) {
          e.preventDefault()
          navigate(dest)
        }
        return
      }

      if (e.key === 'g' && !e.ctrlKey && !e.metaKey && !e.altKey) {
        waitingForG.current = true
        gTimer.current = setTimeout(() => { waitingForG.current = false }, 1000)
        return
      }
    }

    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [navigate])
}
